import { assign, createMachine, MachineConfig } from 'xstate';
import * as actions from '../submitCase/actions';
import * as guards from '../submitCase/guards';
import {
  CaseType,
  CaseTypeContext,
  CaseTypeEvent,
} from '../submitCase/caseType';
import caseTypeStates from '../submitCase/caseType';
import checkoutStates, { CheckoutContext } from '../editCase/checkout';
import { PaymentType } from '../submitCase/selectPaymentType';
import { CheckoutEvent } from '../submitCase/checkout';
import * as RDBCases from '../../services/buildfire/rdb/cases';
import { getDatabase } from 'firebase/database';
import { assignResult } from '../util/actionHelpers';
import { getPriorityInfo as _getPriorityInfo } from '../../services/buildfire/rdb/cases';

export type EditCaseTypeContext = CaseTypeContext & CheckoutContext;

export type EditCaseTypeEvent = CaseTypeEvent | CheckoutEvent;

async function load(context: EditCaseTypeContext) {
  try {
    const { firebaseApp, case: c } = context;
    const db = getDatabase(firebaseApp);

    const invoice = await RDBCases.getInvoice(db, c.id);
    const priorityInfo = await _getPriorityInfo(db);

    return {
      selectedPaymentMethod: invoice.paymentMethodId,
      stripeCustomerId: invoice.stripeCustomerId,
      setupIntent: invoice.setupIntentId
        ? {
            id: invoice.setupIntentId,
          }
        : undefined,
      businessStart: priorityInfo.businessStart,
      businessEnd: priorityInfo.businessEnd,
      priorityAvailable: priorityInfo.enabled,
      hasLoadedPriorityInfo: true,
      priorityFeePrice: priorityInfo.price,
    };
  } catch (e: any) {
    console.error(e);
    throw e;
  }
}

const config: MachineConfig<EditCaseTypeContext, any, EditCaseTypeEvent> = {
  initial: 'caseType',
  context: {
    firebaseApp: null as any,
    userId: '',
    caseType: CaseType.STANDARD,
    priorityAvailable: false,
    hasLoadedPriorityInfo: false,
    email: '',
    name: '',
    caseInfo: {} as any,
    setupIntent: null as any,
    selectedPaymentMethod: '',
    selectedInvoiceAccountUid: '',
    paymentType: PaymentType.CREDIT_CARD,
    case: null as any,
  },

  states: {
    caseType: {
      ...(caseTypeStates as any),
      initial: 'initial',
      states: {
        // Only get invoices if we don't already have them loaded
        initial: {
          always: [
            {
              target: 'selectCaseType',
              cond: 'hasLoadedPriorityInfo',
            },
            {
              target: 'load',
            },
          ],
        },
        load: {
          invoke: {
            src: load,
            onDone: {
              target: 'selectCaseType',
              actions: assignResult,
            },
            onError: {
              target: 'error',
              actions: assign({
                error: (context, ev) => ev.data,
              }),
            },
          },
        },

        selectCaseType: {
          on: {
            SET_CASE_TYPE: {
              actions: 'setCaseType',
            },
          },
        },

        error: {
          on: {
            RETRY: {
              target: 'load',
            },
          },
        },
      },
      on: {
        NEXT: 'checkout',
      },
    },

    checkout: {
      ...(checkoutStates as any),
      onDone: 'submitted',
      on: {
        BACK: 'caseType',
      },
    },

    submitted: {
      type: 'final',
    },

    error: {
      on: {
        RETRY: {
          target: 'caseType',
        },
      },
    },
  },
};

export const editCaseTypeMachine = createMachine<
  EditCaseTypeContext,
  EditCaseTypeEvent
>(config, {
  actions: actions as any,
  guards: {
    ...(guards as any),
    every: (ctx, event, { cond }) => {
      if (cond.type !== 'every') {
        throw new Error('Error: "every" condition expected type to be "every"');
      }
      return cond.guards.every((guardKey) => guards[guardKey](ctx, event));
    },
  },
});
